import type { PriorEducationStatus, TargetDegreeType } from '@teachur/http-client';

import { DEGREE_LEVEL_OPTIONS } from '@new/common/constants/degreeLevels';

export interface SituationOption {
  value: PriorEducationStatus;
  title: string;
  subtitle: string;
  description: string;
}

export const SITUATION_OPTIONS: SituationOption[] = [
  {
    value: 'no_credits',
    title: 'Starting from scratch',
    subtitle: '0 previous college credits',
    description: "You'll begin with the 60-credit Associate degree.",
  },
  {
    value: 'some_credits_no_associate',
    title: 'Fewer than 45 college credits',
    subtitle: '< 45 credits, no earned Associate degree',
    description:
      "We'll apply your credits toward the Associate degree, and assess what remaining general education and total credits you still need.",
  },
  {
    value: 'many_credits_no_associate',
    title: '45+ college credits',
    subtitle: 'At least 45 credits, no earned Associate degree',
    description:
      "We'll apply your credits toward the Associate degree, and build an integrated Associate + Bachelor's path from your remaining total credit requirements.",
  },
  {
    value: 'associate_only',
    title: 'Earned Associate degree',
    subtitle: 'An earned Associate with exactly 60 credits',
    description:
      "Satisfies the first 60 credits and all general education requirements. 60 additional credits remain to earn a Bachelor's degree: 30 major credits, and 30 electives.",
  },
  {
    value: 'associate_plus_credits',
    title: 'Earned Associate + additional credits',
    subtitle: 'An earned Associate degree plus additional credits',
    description:
      "Satisfies the first 60 credits and all general education requirements. Up to 30 eligible credits can apply toward the 30-credit elective block of the Bachelor's degree.",
  },
  {
    value: 'bachelor_or_higher',
    title: "Earned Bachelor's or higher",
    subtitle: "An earned Bachelor's or higher degree",
    description:
      "Satisfies all credit requirements for a Newlane Bachelor's degree outside of the 30 required major credits (10 courses).",
  },
];

export const TARGET_DEGREE_OPTIONS: { value: TargetDegreeType; label: string }[] =
  DEGREE_LEVEL_OPTIONS;

export const PROGRAM_OPTIONS = [
  'B.A. in Philosophy',
  'B.A. in Liberal Arts - Business',
  'B.A. in Liberal Arts - Psychology',
  'B.A. in Liberal Arts - Criminal Justice',
  'B.A. in Liberal Arts - Art History',
];

export const ADDITIONAL_CREDITS_SOURCE_OPTIONS = [
  { value: 'same_institution', label: 'Same institution' },
  { value: 'different_institutions', label: 'Different institutions' },
  { value: 'both', label: 'Both' },
  { value: 'not_sure', label: 'Not sure' },
] as const;

export const situationNeedsCredits = (s: PriorEducationStatus | null): boolean =>
  s === 'some_credits_no_associate' || s === 'many_credits_no_associate';

export const situationNeedsDegree = (s: PriorEducationStatus | null): boolean =>
  s === 'associate_only' || s === 'associate_plus_credits' || s === 'bachelor_or_higher';

export const situationNeedsAdditionalCredits = (s: PriorEducationStatus | null): boolean =>
  s === 'associate_plus_credits';
